import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Alert,
  Chip,
  Divider,
  Link
} from '@mui/material';
import { Close as CloseIcon, Campaign as AnnouncementIcon } from '@mui/icons-material';

interface Announcement {
  _id: string;
  title: string;
  message: string;
  isImportant: boolean;
  createdAt: string | Date;
  createdBy: string | { _id: string; username: string; };
  visibility?: 'public' | 'private';
}

interface AnnouncementModalProps {
  open: boolean;
  onClose: () => void;
  announcements: Announcement[];
  eventTitle?: string;
  isParticipant?: boolean;
  title?: string;
}

// Function to convert URLs in text to clickable links
const renderTextWithLinks = (text: string) => {
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = text.split(urlRegex);
  
  return parts.map((part, index) => {
    if (part.match(urlRegex)) {
      return (
        <Link
          key={index}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ 
            color: 'primary.main',
            textDecoration: 'underline',
            wordBreak: 'break-all',
            '&:hover': {
              textDecoration: 'underline',
              opacity: 0.8
            }
          }}
        >
          {part}
        </Link>
      );
    }
    return part;
  });
};

const AnnouncementModal: React.FC<AnnouncementModalProps> = ({ 
  open, 
  onClose, 
  announcements,
  eventTitle,
  isParticipant = false,
  title
}) => {
  // Filter announcements based on visibility and user participation status
  const visibleAnnouncements = announcements.filter(announcement => {
    // If visibility is not set or is public, show it to everyone
    if (!announcement.visibility || announcement.visibility === 'public') {
      return true;
    }
    // If visibility is private, only show to participants
    if (announcement.visibility === 'private') {
      return isParticipant;
    }
    return true;
  });


  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      disableScrollLock={false}
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '80vh'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        pb: 2,
        borderBottom: '1px solid',
        borderColor: 'divider'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AnnouncementIcon color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {title || (eventTitle ? "活動公告" : "公告")}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ color: 'text.secondary' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2, pb: 3 }}>
        {eventTitle && (
          <Box sx={{ mb: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', mb: 0.5 }}>
              活動名稱
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {eventTitle}
            </Typography>
          </Box>
        )}

        {visibleAnnouncements.length === 0 ? (
          <Box sx={{ 
            py: 6, 
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2
          }}>
            <AnnouncementIcon sx={{ fontSize: 64, color: 'text.disabled' }} />
            <Typography variant="body1" color="text.secondary">
              目前沒有公告
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {visibleAnnouncements.map((announcement, index) => (
              <Box key={announcement._id}>
                <Box sx={{ 
                  p: 2, 
                  bgcolor: announcement.isImportant ? 'error.lighter' : 'background.paper',
                  border: '1px solid',
                  borderColor: announcement.isImportant ? 'error.main' : 'divider',
                  borderRadius: 1
                }}>
                  {/* Announcement Header */}
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.5 }}>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {announcement.title}
                        </Typography>
                        {announcement.isImportant && (
                          <Chip 
                            label="重要" 
                            color="error" 
                            size="small" 
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        )}
                        {announcement.visibility === 'private' && (
                          <Chip 
                            label="私密" 
                            color="secondary" 
                            size="small" 
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        )}
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(announcement.createdAt).toLocaleString('zh-TW', {
                          year: 'numeric',
                          month: '2-digit',
                          day: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Announcement Message */}
                  <Alert 
                    severity={announcement.isImportant ? 'error' : 'info'}
                    icon={<AnnouncementIcon />}
                    sx={{ 
                      '& .MuiAlert-message': { 
                        width: '100%' 
                      }
                    }}
                  >
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {renderTextWithLinks(announcement.message)}
                    </Typography>
                  </Alert>
                </Box>
                {index < visibleAnnouncements.length - 1 && <Divider sx={{ my: 1 }} />}
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AnnouncementModal;
